import React from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import { useTranslation } from '@refinedev/core';
import { admin_type } from '@/api/types/getAdmin';
import useAPI from '@/api/useApi';
import { endPoints } from '@/api/configs';

interface AddAdminModalProps {
  open: boolean;
  onClose: () => void;
  refetch: () => void;
}

interface AdminFormValues {
  name: string;
  email: string;
  role: admin_type;
}

export const AddAdminModal: React.FC<AddAdminModalProps> = ({
  open,
  onClose,
  refetch,
}) => {
  const { translate } = useTranslation();
  const [form] = Form.useForm<AdminFormValues>();
  const { usePost } = useAPI();
  const { mutate: createAdmin, isLoading } = usePost(endPoints.admin());
  const handleOk = async () => {
    const values = await form.getFieldsValue();
    const validatedValues = await form.validateFields();
    if (validatedValues) {
      createAdmin(
        {
          name: values.name,
          email: values.email,
          admin_type: values.role,
        },
        {
          onSuccess: () => {
            form.resetFields();
            onClose();
            refetch();
          },
          onError: (error) => {
            message.error(error.message);
          },
        }
      );
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={translate('listing-admins.title')}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={translate('buttons.create')}
      cancelText={translate('buttons.cancel')}
      okButtonProps={{ loading: isLoading }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label={translate('listing-admins.name')}
          rules={[
            {
              required: true,
              message: translate('pages.errors.required'),
            },
          ]}
        >
          <Input placeholder={translate('listing-admins.name.placeholder')} />
        </Form.Item>
        <Form.Item
          name="email"
          label={translate('listing-admins.email')}
          rules={[
            {
              required: true,
              message: translate('pages.errors.required'),
            },
            {
              type: 'email',
              message: translate('pages.login.errors.validEmail'),
            },
          ]}
        >
          <Input placeholder={translate('listing-admins.email.placeholder')} />
        </Form.Item>
        <Form.Item
          name="role"
          label={translate('listing-admins.role.title')}
          rules={[
            {
              required: true,
              message: translate('pages.errors.required'),
            },
          ]}
        >
          <Select
            placeholder={translate('listing-admins.role.placeholder')}
            options={[
              {
                label: translate('listing-admins.role.ACADEMIC_SUPERVISOR'),
                value: admin_type.ACADEMIC_SUPERVISOR,
              },
              {
                label: translate('listing-admins.role.PROGRAM_HEAD'),
                value: admin_type.PROGRAM_HEAD,
              },
              {
                label: translate('listing-admins.role.SYSTEM_ADMIN'),
                value: admin_type.SYSTEM_ADMIN,
              },
              {
                label: translate('listing-admins.role.ADMIN'),
                value: admin_type.ADMIN,
              },
            ]}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
